import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarDays, MapPin } from "lucide-react";
import { sortedEvents, eventStatus } from "@/eventsData";
import { useLang, scrollToId } from "@/i18n";
import { fmtDate } from "@/api";

const SLIDE_MS = 4000;

export default function CampaignBanner() {
  const { t, lang } = useLang();
  const slides = sortedEvents().filter((ev) => ev.booking_campaign && eventStatus(ev).key !== "ended");
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || slides.length < 2) return undefined;
    const timer = setInterval(() => setIdx((i) => (i + 1) % slides.length), SLIDE_MS);
    return () => clearInterval(timer);
  }, [paused, slides.length]);

  if (!slides.length) return null;
  const ev = slides[idx % slides.length];
  const status = eventStatus(ev);

  return (
    <section className="relative z-10 px-6 md:px-12 -mt-6" data-testid="campaign-banner"
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="max-w-[1400px] mx-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={ev.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45 }}
            className="bg-main text-cream rounded-3xl overflow-hidden grid md:grid-cols-12 shadow-xl shadow-main/20"
            data-testid="campaign-slide"
          >
            {ev.image && (
              <button onClick={() => scrollToId("#rezervare")} data-testid="campaign-image-btn" aria-label={t("popup.cta")}
                className="md:col-span-3 max-h-56 md:max-h-72 overflow-hidden cursor-pointer group/img">
                <img src={ev.image} alt={ev.name} className="w-full h-full object-cover object-top group-hover/img:scale-105 transition-transform duration-700" data-testid="campaign-image" />
              </button>
            )}
            <div className={`${ev.image ? "md:col-span-9" : "md:col-span-12"} p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5`}>
              <div className="flex-1">
                <p className="text-[10px] tracking-[0.25em] uppercase text-terracotta-light font-bold" data-testid="campaign-status">
                  {status.key === "live" ? t("ev.live") : t("ev.upcoming").replace("{d}", status.days)} · {ev.name}
                </p>
                <p className="mt-2 font-serif text-xl md:text-2xl leading-snug" data-testid="campaign-copy">
                  {ev[`campaign_${lang}`] || ev.campaign_en || ev.campaign_ro}
                </p>
                <p className="mt-2 text-xs text-cream/60 flex items-center gap-3">
                  <span className="inline-flex items-center gap-1.5"><CalendarDays size={12} /> {fmtDate(ev.start_date)} – {fmtDate(ev.end_date)}</span>
                  <span className="inline-flex items-center gap-1.5"><MapPin size={12} /> {ev[`location_${lang}`] || ev.location_en || ev.location_ro}</span>
                </p>
              </div>
              <div className="flex flex-col gap-3 shrink-0">
                <button onClick={() => scrollToId("#rezervare")} data-testid="campaign-book-btn"
                  className="rounded-full bg-terracotta text-cream-light font-semibold px-7 py-3.5 hover:bg-terracotta-light transition-all duration-300 text-sm whitespace-nowrap">
                  {t("popup.cta")}
                </button>
                {ev.page && (
                  <Link to={ev.page} data-testid="campaign-details-btn"
                    className="rounded-full border border-cream/40 text-cream font-semibold px-7 py-3 text-center hover:bg-cream hover:text-main transition-all duration-300 text-xs whitespace-nowrap">
                    {t("ev.details")}
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {slides.length > 1 && (
          <div className="mt-3 flex items-center justify-center gap-2" data-testid="campaign-dots">
            {slides.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setIdx(i)}
                aria-label={s.name}
                data-testid={`campaign-dot-${i}`}
                className={`rounded-full transition-all duration-300 ${i === idx % slides.length ? "w-6 h-2.5 bg-terracotta" : "w-2.5 h-2.5 bg-walnut/30 hover:bg-walnut/60"}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
